import React, { useState, useEffect, forwardRef } from 'react';
import RateLimitInfo from './RateLimitInfo';
import rateLimiter from '../services/rateLimiter';

const OrderForm = forwardRef(({ onSubmit, loading, isFormBlocked: propIsFormBlocked, timeUntilUnblock }, ref) => {
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [attemptsInfo, setAttemptsInfo] = useState(null);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [showWarning, setShowWarning] = useState(false);

  // Получение информации о попытках
  useEffect(() => {
    const updateAttemptsInfo = () => {
      const clientIP = rateLimiter.getClientID();
      const info = rateLimiter.getAttemptsInfo(clientIP);
      setAttemptsInfo(info);
    };

    updateAttemptsInfo();
    const interval = setInterval(updateAttemptsInfo, 1000); // Обновляем каждую секунду

    return () => clearInterval(interval);
  }, []);

  // Обновление времени каждую секунду для таймера
  useEffect(() => {
    const timeInterval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(timeInterval);
  }, []);

  // Проверка, заблокирована ли форма
  const isFormBlocked = propIsFormBlocked || (attemptsInfo && attemptsInfo.hasUsedOnce && attemptsInfo.usedAt && (currentTime - attemptsInfo.usedAt) < (3 * 60 * 1000));

  const validateOrderNumber = (number) => {
    const errors = {};
    
    if (!number.trim()) {
      errors.orderNumber = 'Введите номер заказа';
    } else if (number.length < 5) {
      errors.orderNumber = 'Номер заказа должен содержать минимум 5 цифр';
    } else if (!/^\d+$/.test(number)) {
      errors.orderNumber = 'Номер заказа должен содержать только цифры';
    } else if (number.length > 20) {
      errors.orderNumber = 'Номер заказа слишком длинный';
    }
    
    return errors;
  };

  const validateEmail = (emailValue) => {
    // Email опциональный, но если введён - должен быть валидным
    if (emailValue.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailValue)) {
        return 'Введите корректный email адрес';
      }
    }
    return null;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const validationErrors = validateOrderNumber(orderNumber);
    const emailError = validateEmail(email);
    
    if (emailError) {
      validationErrors.email = emailError;
    }
    
    setErrors(validationErrors);
    
    if (Object.keys(validationErrors).length === 0) {
      onSubmit(orderNumber.trim(), email.trim());
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setOrderNumber(value);
    
    // Очищаем ошибку при вводе
    if (errors.orderNumber) {
      setErrors({ ...errors, orderNumber: '' });
    }
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    
    // Очищаем ошибку при вводе
    if (errors.email) {
      setErrors({ ...errors, email: '' });
    }
  };

  return (
    <div className={`card p-6 md:p-8 ${isFormBlocked ? 'opacity-50 bg-gray-50' : ''}`}>
      <div className="text-center mb-6 md:mb-8">
        <div className={`w-14 h-14 md:w-16 md:h-16 ${isFormBlocked ? 'bg-gray-200' : 'bg-primary-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
          <svg className={`w-8 h-8 ${isFormBlocked ? 'text-gray-400' : 'text-primary-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
          </svg>
        </div>
        <h2 className={`text-xl md:text-2xl font-semibold mb-2 ${isFormBlocked ? 'text-gray-500' : 'text-gray-900'}`}>
          {isFormBlocked ? 'Форма заблокирована' : 'Введите номер заказа'}
        </h2>
        <p className={`text-sm md:text-base px-2 ${isFormBlocked ? 'text-gray-400' : 'text-gray-600'}`}>
          {isFormBlocked 
            ? (() => {
                if (!timeUntilUnblock) {
                  return 'Вы уже использовали систему восстановления. Блокировка на 3 минуты.';
                }
                
                const minutes = Math.floor(timeUntilUnblock / (1000 * 60));
                const seconds = Math.floor((timeUntilUnblock % (1000 * 60)) / 1000);
                
                if (minutes > 0) {
                  return `Вы уже использовали систему восстановления. Блокировка на 3 минуты. Осталось: ${minutes}м ${seconds}с.`;
                } else if (seconds > 0) {
                  return `Вы уже использовали систему восстановления. Блокировка на 3 минуты. Осталось: ${seconds}с.`;
                } else {
                  return 'Вы уже использовали систему восстановления. Блокировка на 3 минуты. Разблокировка через несколько секунд.';
                }
              })()
            : 'Номер заказа можно найти в разделе "Мои заказы" на Яндекс Маркете'
          }
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
        <div>
          <label htmlFor="orderNumber" className={`block text-sm font-medium mb-2 ${isFormBlocked ? 'text-gray-400' : 'text-gray-700'}`}>
            Номер заказа
          </label>
          <input
            ref={ref}
            type="text"
            id="orderNumber"
            value={orderNumber}
            onChange={handleInputChange}
            placeholder="470000000"
            className={`input-field text-sm md:text-base ${errors.orderNumber ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''} ${isFormBlocked ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
            disabled={loading || isFormBlocked}
          />
          {errors.orderNumber && (
            <p className="mt-2 text-sm text-red-600">{errors.orderNumber}</p>
          )}
          
          {/* Информация о лимитах */}
          <RateLimitInfo attemptsInfo={attemptsInfo} timeUntilUnblock={timeUntilUnblock} />
        </div>

        {/* Email поле (опциональное) */}
        <div>
          <label htmlFor="email" className={`block text-sm font-medium mb-2 ${isFormBlocked ? 'text-gray-400' : 'text-gray-700'}`}>
            Email (опционально)
            <span className="text-xs text-gray-500 ml-2">для дублирования подписки</span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <input
              type="email"
              id="email"
              value={email}
              onChange={handleEmailChange}
              placeholder="example@email.com"
              className={`input-field text-sm md:text-base pl-10 ${errors.email ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''} ${isFormBlocked ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
              disabled={loading || isFormBlocked}
            />
          </div>
          {errors.email && (
            <p className="mt-2 text-sm text-red-600">{errors.email}</p>
          )}
          <p className="mt-2 text-xs text-gray-500">
            📧 Мы отправим на указанный email дубликат подписки для сохранности
          </p>
          
          {/* Компактное предупреждение с раскрытием */}
          <div className="mt-3">
            <button
              type="button"
              onClick={() => setShowWarning(!showWarning)}
              className="w-full flex items-center justify-between p-2.5 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg transition-colors duration-200"
            >
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-amber-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="text-xs font-medium text-amber-900">
                    Важная информация о сохранении подписки
                  </span>
              </div>
              <svg 
                className={`w-4 h-4 text-amber-600 transition-transform duration-200 ${showWarning ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {/* Раскрывающийся блок */}
            {showWarning && (
              <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg animate-fade-in">
                <p className="text-xs text-amber-800 leading-relaxed">
                  История восстановления сохраняется <span className="font-semibold">только в этом браузере</span> на этом устройстве. 
                  Если вы очистите кеш браузера, переустановите систему или потеряете доступ к этому устройству — 
                  <span className="font-semibold text-amber-900"> восстановить подписку через поддержку будет невозможно</span>. 
                  <br/><br/>
                  <span className="font-semibold text-amber-900">💡 Рекомендуем:</span> Обязательно укажите email для резервной копии или сохраните подписку в безопасном месте!
                </p>
              </div>
            )}
          </div>
        </div>

        <button
          type="submit"
          disabled={loading || !orderNumber.trim() || isFormBlocked}
          className={`w-full text-sm md:text-base ${isFormBlocked 
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
            : 'btn-primary'
          }`}
        >
          {loading ? 'Проверяем заказ...' : isFormBlocked ? 'Форма заблокирована' : 'Получить подписку'}
        </button>
      </form>

      <div className={`mt-6 md:mt-8 p-3 md:p-4 rounded-lg ${isFormBlocked ? 'bg-gray-100' : 'bg-blue-50'}`}>
        <div className="flex items-start">
          <svg className={`w-5 h-5 mt-0.5 mr-3 flex-shrink-0 ${isFormBlocked ? 'text-gray-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h3 className={`text-sm font-medium mb-1 ${isFormBlocked ? 'text-gray-500' : 'text-blue-900'}`}>
              {isFormBlocked ? 'Временная блокировка' : 'Как найти номер заказа?'}
            </h3>
            <p className={`text-sm ${isFormBlocked ? 'text-gray-400' : 'text-blue-700'}`}>
              {isFormBlocked 
                ? 'Для обеспечения безопасности системы восстановления действует временная блокировка на 3 минуты после использования.'
                : 'Откройте Яндекс Маркет → "Мои заказы" → выберите нужный заказ → номер указан в деталях заказа'
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
});

OrderForm.displayName = 'OrderForm';

export default OrderForm;
